import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  SquareCheckBig,
  ChevronRight,
  BarChart3,
  ArrowRight,
  Clock,
  Circle,
  Flag,
  MailCheck,
  Plus,
  Link2,
  ExternalLink,
} from 'lucide-react';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { PatientLink } from '@/components/ui/PatientLink';
import { TaskDoneButton } from '@/components/tasks/TaskDoneButton';
import * as dashboardSvc from '@/services/dashboard.service';
import * as tasksSvc from '@/services/tasks.service';
import type { Task } from '@/services/tasks.service';
import { CompleteModal, TaskModal } from '@/pages/TasksPage';
import { canCompleteTask, statusVariant, TASK_BUCKET_PENDING, taskDueDate } from '@/pages/tasks/tasksShared';
import * as commSvc from '@/services/communications.service';
import { usePageContext } from '@/hooks/usePageContext';
import { useMyTaskCounts } from '@/hooks/useMyTaskCounts';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { useCommonLinkImage } from '@/hooks/useCommonLinkImage';
import { NotificationsCard } from '@/pages/dashboard/NotificationsCard';

import { CHANNEL_STYLES, channelIcon, relativeTime } from '@/pages/inbox/inboxShared';
import { AlertsBanner } from '@/components/dashboard/AlertsBanner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Task due dates are calendar dates, not timestamps — take a Date already
 * parsed by taskDueDate (which reads a date-only "YYYY-MM-DD" as local midnight)
 * and never render a clock time, which was previously fabricated from a UTC
 * parse and also pushed every date a day early.
 */
function formatDueDate(due: Date | null): string {
  if (!due) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(due);
  day.setHours(0, 0, 0, 0);

  const diffDays = Math.round((day.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  return day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const todayFormatted = new Date().toLocaleDateString('en-US', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

// ─── Priority / category / channel helpers ────────────────────────────────────

const TASK_PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-red-500',
  high: 'text-red-500',
  '4': 'text-red-500',
  '3': 'text-red-500',
  normal: 'text-amber-500',
  '2': 'text-amber-500',
  low: 'text-green-500',
  '1': 'text-green-500',
};



// ─── Inbox row (shared by Inbox card + DashboardInboxModal) ───────────────────

function InboxMessageRow({ entry, onSelect, onMarkRead }: { entry: commSvc.CommLogEntry; onSelect: (entry: commSvc.CommLogEntry) => void; onMarkRead?: (entry: commSvc.CommLogEntry) => void }) {
  const ch = CHANNEL_STYLES[entry.type] ?? { label: entry.type?.toUpperCase() ?? 'MSG', bg: 'bg-gray-100 text-gray-700' };
  const patientName =
    entry.patientFirstName || entry.patientLastName
      ? `${entry.patientFirstName ?? ''} ${entry.patientLastName ?? ''}`.trim()
      : 'Unknown';
  const unread = Number(entry.wasRead ?? 0) === 0;
  const previewText = (() => {
    if (!entry.message) return null;
    let plain = entry.message.replace(/<[^>]*>/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    // Strip quoted reply chains (e.g. "On Mon, Jan 1, 2024 at 1:00 PM Name <email> wrote:")
    plain = plain.replace(/\s*On\s+\w{3},\s+\w{3,9}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(AM|PM)?\s*.*/i, '').trim();
    // Also strip "---------- Forwarded message ----------" and everything after
    plain = plain.replace(/\s*-{5,}\s*Forwarded message\s*-{5,}.*/i, '').trim();
    if (!plain) return null;
    return plain.length > 200 ? plain.slice(0, 200) + '…' : plain;
  })();

  const rowContent = (
    <div
      className={[
        'flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors border-l-[3px]',
        unread
          ? 'border-l-blue-500 bg-blue-50/30 hover:bg-blue-50/60'
          : 'border-l-transparent hover:bg-muted/30',
      ].join(' ')}
      onClick={() => onSelect(entry)}
    >
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
          {patientName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        {unread && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className={
              unread
                ? 'text-sm font-bold text-slate-950 truncate'
                : 'text-sm font-medium text-slate-500 truncate'
            }
          >
            {patientName}
          </span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${ch.bg}`}>
            {channelIcon(ch.icon)}
            {ch.label}
          </span>
        </div>
        <p
          className={
            unread
              ? 'text-xs font-semibold text-slate-700 truncate'
              : 'text-xs text-muted-foreground truncate'
          }
        >
          {entry.subject || '(No subject)'}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span
          className={
            unread
              ? 'text-[10px] font-semibold text-blue-600 whitespace-nowrap'
              : 'text-[10px] text-muted-foreground whitespace-nowrap'
          }
        >
          {relativeTime(entry.createdAt)}
        </span>
        <div className="flex items-center gap-1">
          {unread && onMarkRead && (
            <button
              type="button"
              title="Mark as read"
              className="p-0.5 rounded hover:bg-blue-100 text-blue-500 hover:text-blue-700 transition-colors"
              onClick={(e) => { e.stopPropagation(); onMarkRead(entry); }}
            >
              <MailCheck className="h-3.5 w-3.5" />
            </button>
          )}
          <Flag className="h-3 w-3 text-amber-500" />
        </div>
      </div>
    </div>
  );

  return (
    <HoverCardPrimitive.Root openDelay={300} closeDelay={150}>
      <HoverCardPrimitive.Trigger asChild>
        {rowContent}
      </HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="left"
          sideOffset={8}
          className="z-50 w-72 rounded-md border border-slate-200 bg-white p-4 shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-slate-900 truncate">{patientName}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${ch.bg}`}>
              {channelIcon(ch.icon)}
              {ch.label}
            </span>
          </div>
          {entry.subject && (
            <p className="text-xs font-medium text-slate-700 mb-1">{entry.subject}</p>
          )}
          {previewText ? (
            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">{previewText}</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">No message content</p>
          )}
          <p className="text-[10px] text-muted-foreground mt-2">{relativeTime(entry.createdAt)}</p>
          <HoverCardPrimitive.Arrow className="fill-white" />
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}

// ─── DashboardInboxModal ──────────────────────────────────────────────────────

const INBOX_MODAL_PAGE_SIZE = 25;

// Card column spans on the 12-column grid (single column below lg). With the
// Alerts module off: Messages and Tasks side by side from lg, as always. With
// it on: the three cards share one row from xl (1280px); between lg and xl the
// first two stay side by side and Alerts takes the full row beneath — three
// across at 1024px with the sidebar open would leave each card ~225px wide.
// Whole literals so Tailwind's scanner and DashboardPage.desktopFreeze.test.ts
// can both read them.
const TWO_UP_SPAN = 'lg:col-span-6';
const THREE_UP_SPAN = 'lg:col-span-6 xl:col-span-4';
const ALERTS_CARD_SPAN = 'lg:col-span-12 xl:col-span-4';

function DashboardInboxModal({
  isOpen,
  onClose,
  onSelect,
  onMarkRead,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (entry: commSvc.CommLogEntry) => void;
  onMarkRead?: (entry: commSvc.CommLogEntry) => void;
}) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isOpen) setPage(1);
  }, [isOpen]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['dashboard-inbox-modal', page],
    queryFn: () => commSvc.getMyInbox({ page, pageSize: INBOX_MODAL_PAGE_SIZE }),
    enabled: isOpen,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const items = data?.data ?? [];
  const meta = data?.meta ?? {};
  const totalPages = Number(meta.totalPages ?? 1) || 1;
  const hasNext = Boolean(meta.hasNext) || page < totalPages;
  const hasPrev = page > 1;

  const handleSelect = (entry: commSvc.CommLogEntry) => {
    onSelect(entry);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Inbox"
      size="xl"
      bodyClassName="p-0"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-muted-foreground">
            {isLoading ? 'Loading…' : `Page ${page} of ${totalPages}`}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={!hasPrev || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" disabled={!hasNext || isFetching} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      }
    >
      <div className="divide-y divide-border min-h-[400px]">
        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="px-5 py-8 text-center text-sm text-rose-600">Unable to load inbox.</p>
        ) : items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">No messages in your inbox</p>
        ) : (
          items.map((entry) => (
            <InboxMessageRow key={entry.commLogId} entry={entry} onSelect={handleSelect} onMarkRead={onMarkRead} />
          ))
        )}
      </div>
    </Modal>
  );
}

// ─── Common Links tiles ───────────────────────────────────────────────────────

// Color-blocked palette for the dashboard Common Links tiles. Tiles cycle
// through these so the grid reads as a neat, varied set of quick-access blocks.
const LINK_TILE_COLORS = [
  'from-blue-500 to-blue-600',
  'from-indigo-500 to-indigo-600',
  'from-violet-500 to-violet-600',
  'from-emerald-500 to-emerald-600',
  'from-amber-500 to-amber-600',
  'from-rose-500 to-rose-600',
  'from-cyan-500 to-cyan-600',
  'from-fuchsia-500 to-fuchsia-600',
];

/**
 * A link's image. `image` is a file name in the account's dashboard folder
 * (legacy's upload), not a URL, so it loads through the API.
 */
function CommonLinkTileImage({ fileName }: { fileName: string }) {
  const url = useCommonLinkImage(fileName);
  if (!url) return null;
  return <img src={url} alt="" className="w-8 h-8 rounded object-cover bg-white/20 flex-shrink-0" />;
}

function CommonLinksTiles({ links }: { links: dashboardSvc.CommonLink[] }) {
  return (
    <Card className="border-border/80 shadow-sm">
      <CardHeader className="pb-3 pt-4 px-5">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Common Links</span>
        </div>
      </CardHeader>
      <CardBody className="px-5 pb-5 pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {links.map((link, i) => {
            const color = LINK_TILE_COLORS[i % LINK_TILE_COLORS.length];
            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                title={link.description || link.title}
                className={`group relative flex flex-col justify-between min-h-[88px] p-3.5 rounded-lg bg-gradient-to-br ${color} text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden`}
              >
                <ExternalLink className="absolute top-2.5 right-2.5 h-3.5 w-3.5 text-white/60 group-hover:text-white transition-colors" />
                {link.image && <CommonLinkTileImage fileName={link.image} />}
                <span className="text-sm font-semibold leading-snug mt-2 line-clamp-2 pr-4">
                  {link.title}
                </span>
              </a>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [inboxModalOpen, setInboxModalOpen] = useState(false);
  const updateAIContext = usePageContext('Dashboard');

  const openPatientCommLog = useCallback((entry: commSvc.CommLogEntry) => {
    if (Number(entry.wasRead ?? 0) === 0 && entry.commLogId) {
      void commSvc.markInboxEntryRead(entry.commLogId, 'inbox')
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-my-inbox'] });
          queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
          queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
        })
        .catch(() => { /* silent */ });
    }

    if (entry.patientId) {
      navigate(`/patients/${entry.patientId}?section=commlog&commLogId=${entry.commLogId}`);
      return;
    }

    navigate('/messages');
  }, [navigate, queryClient]);

  const markReadOnly = useCallback((entry: commSvc.CommLogEntry) => {
    if (!entry.commLogId) return;
    void commSvc.markInboxEntryRead(entry.commLogId, 'inbox')
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-my-inbox'] });
        queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-inbox-modal'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
        queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
      })
      .catch(() => { /* silent */ });
  }, [queryClient]);

  // ── Data queries ─────────────────────────────────────────────────────────
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardSvc.getSummary(),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
  });

  // flTasks=FL_PENDING lists everything still awaiting action — Past Due, Due
  // Today and Upcoming. Without it the endpoint defaults to FL_UPCOMING, which
  // silently hid every overdue task from a card titled "Pending Tasks".
  // Soonest-first so the most overdue work is at the top.
  const { data: tasksData } = useQuery({
    queryKey: ['dashboard-my-tasks'],
    queryFn: () => tasksSvc.getMyTasks({
      page: 1,
      perPage: 7,
      myOnly: true,
      flTasks: TASK_BUCKET_PENDING,
      sortBy: 'lastDue',
      sortDir: 'asc',
      // The card shows no total, and counting one doubles the request.
      withTotal: false,
    }),
    staleTime: 60_000,
    retry: false,
  });

  // readStatus: 'unread' is the whole point of this card. Without it
  // folder=inbox returns every message ever addressed to the user — read
  // included, no type gate, no date window — so items dealt with months ago
  // keep reappearing. Same request the /inbox Unread tab already makes.
  // isError matters as much as the rows here: without it a failed request and
  // a genuinely empty inbox both render "You have no unread messages.", which
  // is how a 500 on this endpoint got reported as a phantom unread count.
  const { data: messagesData, isError: messagesFailed } = useQuery({
    queryKey: ['dashboard-my-inbox', 'unread'],
    queryFn: () => commSvc.getMyInbox({ pageSize: 7, readStatus: 'unread' }),
    staleTime: 60_000,
    retry: false,
  });

  // The badge above the list has to count the list. /dashboard/summary's
  // unreadMessages is practice-wide (inbound patient messages, global
  // comm_log.wasRead) and is the number the Communication Center badge shows:
  // it counts messages nobody addressed to this user, and the card's mark-read
  // writes comm_log_recipients.wasRead, so it could never be decremented from
  // here. inbox-counts is the recipient-scoped population these rows come
  // from, keyed exactly as Sidebar and InboxPage key it so all three read one
  // cache entry and cannot drift apart.
  const { data: inboxCounts } = useQuery({
    queryKey: ['inbox-counts'],
    queryFn: () => commSvc.getInboxCounts(),
    staleTime: 60_000,
    retry: false,
  });

  // Same query as the sidebar's Tasks count (useMyTaskCounts owns the key); the
  // sidebar leaves upcoming tasks out, so its number can be lower than this one.
  const { data: taskCounts } = useMyTaskCounts();

  const { enabled: alertsModuleOn } = useModuleAccess('alerts');
  const cardSpan = alertsModuleOn ? THREE_UP_SPAN : TWO_UP_SPAN;

  const { data: commonLinks } = useQuery({
    queryKey: ['dashboard-common-links'],
    queryFn: dashboardSvc.getCommonLinks,
    staleTime: 5 * 60_000,
  });

  // ── Derived data ─────────────────────────────────────────────────────────
  const tasks = tasksData?.data ?? [];
  const messages = Array.isArray(messagesData?.data) ? messagesData.data : [];
  const totalActivePlans = data?.plansByStage.reduce((s, st) => s + st.count, 0) ?? 0;
  const tasksDueToday = data?.tasksDueToday ?? 0;
  // `actionable`, not `pending` — the latter is a legacy alias of the Upcoming
  // count and would disagree with the rows now listed below it.
  const pendingTaskCount = taskCounts?.actionable ?? tasks.length;
  const unreadInbox = inboxCounts?.unread ?? 0;

  // ── AI context ──────────────────────────────────────────────────────────
  useEffect(() => {
    updateAIContext({
      dataSummary: data
        ? `Appointments today: ${data.todayAppointments}, Tasks due: ${tasksDueToday}, Unread: ${unreadInbox}, Active plans: ${totalActivePlans}`
        : 'Loading...',
    });
  }, [data, tasksDueToday, unreadInbox, totalActivePlans, updateAIContext]);

  // ── Task detail modal state ────────────────────────────────────────────
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [completeTarget, setCompleteTarget] = useState<Task | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);

  // Every task mutation on this page refreshes the same views, including the
  // sibling /tasks dashboard card so the two never disagree.
  const invalidateTaskViews = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-my-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-task-counts'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-dashboard-counts'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-dashboard-my-tasks'] });
  }, [queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" label="Loading dashboard..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <Alert variant="error" title="Unable to load dashboard">
          {error instanceof Error ? error.message : 'Failed to load dashboard data.'}
        </Alert>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden max-md:h-auto max-md:overflow-visible">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-border px-6 py-4 flex items-center justify-between flex-shrink-0 max-md:flex-col max-md:items-start max-md:gap-3 max-md:px-4">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{todayFormatted}</p>
        </div>
        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => navigate('/leads/new')}
          >
            Add Patient
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/reports/my-reports')}
            leftIcon={<BarChart3 className="h-3.5 w-3.5" />}
          >
            Reports
          </Button>
        </div>
      </div>

      {/* ── Main scrollable content ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <AlertsBanner />
        <div className="px-6 py-5 max-md:px-4 max-md:py-4">
        {/* Two-up from lg only. Below 1024 the global sidebar is still in flow at
            16rem, so a half-width panel here is ~222px at 768 — narrower than the
            same panel on a phone. Stacking at md would fix phones and leave the
            tablet band worse. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

          {/* ── Inbox card ──────────────────────────────────────────────── */}
          <div className={cardSpan}>
            <Card className="border-border/80 shadow-sm h-full">
              <CardHeader className="pb-3 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Unread Messages</span>
                    {unreadInbox > 0 && (
                      <span className="inline-flex items-center justify-center h-5 px-1.5 text-[10px] font-bold bg-blue-600 text-white rounded-full min-w-[20px]">
                        {unreadInbox}
                      </span>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs text-primary h-7 px-2 gap-1" onClick={() => navigate('/inbox')}>
                    View All <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="px-0 pb-0">
                <div className="divide-y divide-border">
                  {messagesFailed ? (
                    <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                      Messages couldn't be loaded. Refresh the page. If this keeps happening, contact your system administrator.
                    </p>
                  ) : messages.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-muted-foreground">You have no unread messages.</p>
                  ) : (
                    messages.slice(0, 7).map((entry) => (
                      <InboxMessageRow key={entry.commLogId} entry={entry} onSelect={openPatientCommLog} onMarkRead={markReadOnly} />
                    ))
                  )}
                </div>
                <div className="px-5 py-3 border-t border-border">
                  <Button variant="ghost" size="sm" className="w-full text-xs text-primary gap-1 h-8" onClick={() => navigate('/messages')}>
                    Open Comm Center <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* ── Pending Tasks card ──────────────────────────────────────── */}
          <div className={cardSpan}>
            <Card className="border-border/80 shadow-sm h-full">
              <CardHeader className="pb-3 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Pending Tasks</span>
                    {pendingTaskCount > 0 && (
                      <span className="inline-flex items-center justify-center h-5 px-1.5 text-[10px] font-bold bg-amber-500 text-white rounded-full min-w-[20px]">
                        {pendingTaskCount}
                      </span>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs text-primary h-7 px-2 gap-1" onClick={() => navigate('/tasks/all')}>
                    View All <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="px-0 pb-0">
                <div className="divide-y divide-border">
                  {tasks.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-muted-foreground">No pending tasks</p>
                  ) : (
                    tasks.slice(0, 7).map((task) => {
                      const prioKey = String(task.priority);
                      const prioColor = TASK_PRIORITY_COLORS[prioKey] ?? 'text-amber-500';
                      const due = taskDueDate(task);
                      // `||`, not `??`: the API maps name as (string)($row->name ?? ''),
                      // so canned tasks can legitimately arrive with an empty name.
                      const taskLabel = task.name?.trim() || `Task #${task.planTaskID}`;
                      return (
                        <div
                          key={task.planTaskID}
                          className="flex items-start gap-2.5 px-5 py-3 cursor-pointer transition-colors group hover:bg-muted/30"
                          onClick={() => setSelectedTask(task)}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {task.derivedStatus === 'Completed' ? (
                              <SquareCheckBig className="h-4 w-4 text-green-500" />
                            ) : (
                              <Circle className={`h-4 w-4 ${prioColor}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-xs font-medium leading-snug text-foreground">
                              {taskLabel}
                            </p>
                            {task.patientName && ((task.taskPatientID ?? 0) > 0 ? (
                              // The truncate/overflow lives on the <p>, not the
                              // anchor: a `block` anchor stretched to the full
                              // row width, so clicks anywhere right of the name
                              // opened the patient instead of the task row.
                              // Inline keeps the hit area on the text itself.
                              <p className="mt-0.5 truncate text-[10px]">
                                <PatientLink
                                  patientId={task.taskPatientID!}
                                  patientName={task.patientName}
                                  className="text-blue-600 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {task.patientName}
                                </PatientLink>
                              </p>
                            ) : (
                              // A name with taskPatientID=0 comes from the plan's
                              // patient — displayable, but there is no id to link.
                              <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{task.patientName}</p>
                            ))}
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <Badge variant={statusVariant(task.derivedStatus)} size="sm">
                                {task.derivedStatus ?? 'N/A'}
                              </Badge>
                              {due && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-2.5 w-2.5" />
                                  {formatDueDate(due)}
                                </span>
                              )}
                            </div>
                          </div>
                          {canCompleteTask(task) && (
                            <TaskDoneButton
                              taskId={task.planTaskID}
                              size="xs"
                              className="mt-0.5 shrink-0"
                              onComplete={() => setCompleteTarget(task)}
                              onRescheduled={invalidateTaskViews}
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="px-5 py-3 border-t border-border">
                  <Button variant="ghost" size="sm" className="w-full text-xs text-primary gap-1 h-8" onClick={() => setShowCreateTask(true)}>
                    Add New Task <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* ── Alerts card ─────────────────────────────────────────────── */}
          {alertsModuleOn && (
            <div className={ALERTS_CARD_SPAN}>
              <NotificationsCard />
            </div>
          )}


          {/* ── Common Links tiles (full width) ─────────────────────────── */}
          {commonLinks && commonLinks.length > 0 && (
            <div className="lg:col-span-12">
              <CommonLinksTiles links={commonLinks} />
            </div>
          )}

        </div>
        </div>
      </div>
      </div>
    <DashboardInboxModal
      isOpen={inboxModalOpen}
      onClose={() => setInboxModalOpen(false)}
      onSelect={openPatientCommLog}
      onMarkRead={markReadOnly}
    />
    {selectedTask && (
      <TaskModal
        item={selectedTask}
        onClose={() => setSelectedTask(null)}
        onSaved={() => {
          setSelectedTask(null);
          invalidateTaskViews();
        }}
        users={[]}
      />
    )}
    {showCreateTask && (
      <TaskModal
        onClose={() => setShowCreateTask(false)}
        onSaved={() => {
          setShowCreateTask(false);
          invalidateTaskViews();
        }}
        users={[]}
      />
    )}
    {completeTarget && (
      <CompleteModal
        task={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onSaved={invalidateTaskViews}
      />
    )}
    </>
  );
}

